import { Injectable, signal, WritableSignal } from '@angular/core';
import { UserCredential } from 'firebase/auth';
import { HttpService } from '@services/helper/http.service';
import { PreferencesService } from '@services/account/preferences.service';
import { AuthService } from '@services/auth/auth.service';
import { FeatureService } from '@services/feature-toggle/feature.service';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { AW } from '@airwallet/types';

@Injectable({
    providedIn: 'root'
})
export class SamlService {
    samlMembershipOwners: WritableSignal<AW.Misc.UidName[] | null> = signal(this.localStorageService.getItem('samlMembershipOwners') ?? null);
    loadingNewOwner: WritableSignal<boolean> = signal(false);
    chosenUid: WritableSignal<string> = signal('');

    constructor(
        private http: HttpService,
        private preferencesService: PreferencesService,
        private authService: AuthService,
        private localStorageService: LocalStorageService,
        private featureService: FeatureService
    ) {}

    /**
     * Sets up the SAML service by fetching and storing SAML membership owners.
     * This is called from the sidebar component, as it needs to be initialized early.
     * It waits for the AuthService permissions to be ready before proceeding, as it needs the authenticatedUser to be set,
     * which is done in AuthService after sign-in.
     */
    async setupSamlService() {
        await this.authService.permissionsReady;
        const dashboardOwnerUser: DashboardOwnerUser = this.localStorageService.getItem('dashboardOwnerUser')!;
        this.chosenUid.set(dashboardOwnerUser.uid);

        if (!this.localStorageService.getItem('samlMembershipOwners')) {
            this.loadingNewOwner.set(true);
            const uidName: AW.Misc.UidName[] = await this.getOwners();
            this.localStorageService.setItem('samlMembershipOwners', uidName);
            this.samlMembershipOwners.set(uidName);
            this.loadingNewOwner.set(false);
        }
    }

    /**
     * Extracts roles from a SAML response.
     *
     * Parses the SAML response to find and validate roles. If a role is `owner`,
     * it returns only that role. Otherwise, it returns all valid roles.
     *
     * @param result - The `UserCredential` containing the SAML response.
     * @returns An array of valid `AW.Account.Saml.AppRole` objects.
     */
    extractRolesFromSamlResponse(result: UserCredential): AW.Account.Saml.AppRole[] {
        const rawInfo = (result as any)._tokenResponse?.rawUserInfo;
        if (rawInfo) {
            const parsed = JSON.parse(rawInfo);

            // Find the property that ends with "/claims/role"
            const roleKey: string | undefined = Object.keys(parsed).find(k => k.endsWith('/claims/role'));

            const mappedRoles: AW.Account.Saml.AppRole[] = [];
            if (roleKey) {
                let roles = parsed[roleKey] || [];
                const uidRegex = /^(?:[a-zA-Z0-9]{28}|[a-zA-Z0-9]{28}_operated_by_[a-zA-Z0-9]{28})$/; // Regex to match 28-character UID with optional _operated_by_ suffix

                // Ensure roles is always an array
                if (!Array.isArray(roles)) {
                    roles = [roles];
                }

                for (const entry of roles) {
                    const [groupId, role] = entry.split(',');
                    // TODO: This list should ideally come from the backend or a shared constants file to ensure consistency across the codebase
                    const roles: AW.Account.Role[] = ['editor', 'owner', 'technician', 'viewer', 'user', 'bookkeeper'];
                    if (uidRegex.test(groupId) && roles.includes(role as AW.Account.Role)) {
                        if (role === 'owner') {
                            return [{ groupId, role: role as AW.Account.Role }]; // Return immediately if role is 'owner'
                        }
                        mappedRoles.push({ groupId, role: role as AW.Account.Role });
                    } else {
                        console.warn(`Invalid groupId or role detected: groupId=${groupId}, role=${role}`);
                    }
                }
            }

            console.log('Mapped roles:', mappedRoles);
            return mappedRoles;
        }
        return [];
    }

    /**
     * Extracts SAML info from a parsed info object, ignoring the schema prefix.
     * @param info The object containing SAML parsed info.
     * @returns An object with the extracted values.
     */
    extractInfo(info: Record<string, string>): AW.Account.Saml.ExtractedInfo {
        let firstName = '';
        let lastName = '';
        let phoneNumber = '';

        // Iterate over every key in the object
        for (const key in info) {
            if (Object.prototype.hasOwnProperty.call(info, key)) {
                if (key.endsWith('givenname')) {
                    firstName = info[key];
                } else if (key.endsWith('surname')) {
                    lastName = info[key];
                } else if (key.endsWith('phone')) {
                    phoneNumber = info[key];
                }
            }
        }

        // Process the role claim if it was found

        return { firstName, lastName, phoneNumber };
    }

    async changeDashboard() {
        this.loadingNewOwner.set(true);
        this.localStorageService.setItem('owner', this.chosenUid());
        const { claims } = await this.authService.setLocalStorage(this.authService.authenticatedUser, undefined, this.chosenUid());
        // The backend rejects requests whose accessmap header doesn't hash-match the chosen
        // owner's access_map_hash claim, so the previous owner's map must be replaced before
        // any on-behalf request (loadPreferences/fetchTrials) fires.
        await this.featureService.refreshAccessMap();
        await Promise.all([this.preferencesService.loadPreferences(), this.authService.setDashboardOwnerPermissions(claims!)]);
        if (this.authService.isAdmin()) {
            const controlledUser: AW.ControlUid.ControlledDashboardOwnerUser = (await this.getControlledDashboardOwnerUser(this.chosenUid()))!;
            this.localStorageService.setItem('controlled_user', controlledUser);
        }
        window.location.reload();
    }

    async getControlledDashboardOwnerUser(chosenUid: string): Promise<AW.ControlUid.ControlledDashboardOwnerUser | null> {
        return await this.http.dynamicHttp(`api_support/get_controlled_dashboard_owner_user?owner=${chosenUid}&member=${this.authService.authenticatedUser.uid}`, 'get', { skipOwner: true });
    }

    async validateSamlAccess(roles: AW.Account.Saml.AppRole[]): Promise<void> {
        return await this.http.dynamicHttp('api_account/saml/validate', 'post', { body: { samlRoles: roles } });
    }

    async createUserWithSamlResponse(memberInfo: AW.Account.Saml.MemberInfo, roles: AW.Account.Saml.AppRole[]): Promise<void> {
        return await this.http.dynamicHttp('api_account/saml', 'post', { body: { roles: roles, memberInfo } });
    }

    async getOwners(): Promise<AW.Misc.UidName[]> {
        return await this.http.dynamicHttp(`api_account/saml/owners`, 'get', { skipOwner: true });
    }

    async removePasswordProvider(): Promise<void> {
        return await this.http.dynamicHttp('api_account/saml/remove_password_provider', 'get');
    }
}
